import { Link } from "react-router";
import { Mail, Phone, MapPin, Clock, Send, Plus, Minus, ArrowRight } from "lucide-react";
import { useState } from "react";
import { useInViewOnce } from "../hooks/useInViewOnce";
import { cn } from "../components/ui/utils";
import { HEADLINE_GRADIENT } from "../components/GradientSectionHeading";

function AccordionItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="w-full border-b border-[#131312]/10 transition-all duration-300">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-start justify-between text-left group"
      >
        <span className="text-xl font-normal text-[#131312] group-hover:opacity-70 transition-opacity pr-8">
          {question}
        </span>
        <div className="flex-shrink-0 text-[#131312]/40 group-hover:text-[#131312] transition-colors mt-1.5">
          {isOpen ? <Minus className="size-5" /> : <Plus className="size-5" />}
        </div>
      </button>
      <div className={cn(
        "overflow-hidden transition-all duration-500 ease-in-out",
        isOpen ? "max-h-[500px] pb-5 opacity-100" : "max-h-0 opacity-0"
      )}>
        <p className="text-[#131312]/60 leading-relaxed text-lg w-full">
          {answer}
        </p>
      </div>
    </div>
  );
}

export function ContactPage() {
  const { ref: headlineRef, inView: headlineInView } = useInViewOnce({ threshold: 0.28, rootMargin: "0px 0px -10% 0px" });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/send-contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("Server error");
      setSubmitted(true);
    } catch {
      setError("Failed to send message. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="min-h-screen">
      <section className="py-20 bg-[#FBFAF7]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h1 ref={headlineRef} className="text-[48px] sm:text-[60px] md:text-[70px] lg:text-[78.1px] font-semibold mb-8 leading-tight text-[#1C1C1C]">
            <span
              className={cn("gradient-text-animate inline", headlineInView && "gradient-text-animate--play")}
              style={{ backgroundImage: HEADLINE_GRADIENT }}
            >
              Get in Touch
            </span>
          </h1>
          <p
            className="font-bold leading-[25px] text-[#1C1C1C] mx-auto"
            style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: '18.6px',
              maxWidth: '627px',
              letterSpacing: '-0.5px'
            }}
          >
            Have questions about our packaging solutions? We're here to help. <br /> Reach out to our team and we'll respond within 24 hours.
          </p>
        </div>
      </section>

      <section className="pb-24 bg-[#FBFAF7] border-b border-[#131312]/5">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
            <div className="bg-[#F7F4ED] rounded-3xl p-8 border border-[#131312]/5 hover:shadow-xl transition-all">
              <div className="size-16 bg-[#131312] text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#131312]/10">
                <Mail className="size-8" />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-[#131312]">Email Us</h3>
              <p className="text-muted-foreground mb-6">Send us an email and we'll respond promptly</p>
              <div className="flex flex-col gap-3">
                <a href="mailto:info@colourclubprinters.com" className="text-[#131312] font-semibold hover:opacity-70 transition-opacity">
                  info@colourclubprinters.com
                </a>
                <a href="mailto:colourclubprinter@gmail.com" className="text-[#131312] font-semibold hover:opacity-70 transition-opacity">
                  colourclubprinter@gmail.com
                </a>
              </div>
            </div>

            <div className="bg-[#F7F4ED] rounded-3xl p-8 border border-[#131312]/5 hover:shadow-xl transition-all">
              <div className="size-16 bg-[#131312] text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#131312]/10">
                <Phone className="size-8" />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-[#131312]">Call Us</h3>
              <p className="text-muted-foreground mb-6">Speak directly with our packaging experts</p>
              <div className="flex flex-col gap-3 text-[#131312] font-semibold">
                <a href="tel:+92418750239" className="hover:opacity-70 transition-opacity">
                  Tel: +92-41-8750239
                </a>
                <a href="tel:+92418850239" className="hover:opacity-70 transition-opacity">
                  Tel: +92-41-8850239
                </a>
                <a href="tel:+923008666239" className="hover:opacity-70 transition-opacity">
                  Mobile: +92-300-8666239
                </a>
              </div>
            </div>

            <div className="bg-[#F7F4ED] rounded-3xl p-8 border border-[#131312]/5 hover:shadow-xl transition-all">
              <div className="size-16 bg-[#131312] text-white rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-[#131312]/10">
                <MapPin className="size-8" />
              </div>
              <h3 className="text-2xl font-semibold mb-3 text-[#131312]">Visit Us</h3>
              <p className="text-muted-foreground mb-6">Schedule a factory tour and see our capabilities</p>
              <p className="font-semibold text-[#131312] leading-relaxed">
                Madina Market, Sugar Mor, Near PSO Pump, Sheikhupura Road, Nishatabad, Faisalabad, Pakistan
              </p>
            </div>

            <div className="bg-[#F7F4ED] rounded-3xl p-8 border border-[#131312]/5 hover:shadow-xl transition-all lg:col-span-3">
              <h3 className="text-2xl font-semibold mb-3 text-[#131312]">Business information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                <p><span className="font-semibold text-[#131312]">Commercial brand:</span> Colour Club Printers</p>
                <p><span className="font-semibold text-[#131312]">Legal supplier:</span> Paper Printing Services</p>
                <p><span className="font-semibold text-[#131312]">FSC certificate holder:</span> Paper Printing Services</p>
                <p><span className="font-semibold text-[#131312]">Sales:</span> <a href="mailto:info@colourclubprinters.com" className="hover:opacity-70 transition-opacity">info@colourclubprinters.com</a></p>
                <p><span className="font-semibold text-[#131312]">Legal/compliance:</span> <a href="mailto:info@paperprintingservices.com" className="hover:opacity-70 transition-opacity">info@paperprintingservices.com</a></p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-4xl font-semibold leading-[1.1] tracking-tight mb-8">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-[#131312]">Your Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F7F4ED] border border-[#131312]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#131312]/20"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#131312]">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F7F4ED] border border-[#131312]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#131312]/20"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#131312]">Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F7F4ED] border border-[#131312]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#131312]/20"
                    placeholder="+92 300 8666239"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#131312]">Subject *</label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-3 bg-[#F7F4ED] border border-[#131312]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#131312]/20"
                    placeholder="General Inquiry"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-[#131312]">Message *</label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    className="w-full px-4 py-3 bg-[#F7F4ED] border border-[#131312]/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#131312]/20 resize-none"
                    placeholder="Tell us about your packaging needs..."
                  />
                </div>

                {error && (
                  <p className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={loading || submitted}
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-[#131312] text-white transition-all hover:bg-black rounded-xl font-bold uppercase tracking-wide shadow-lg shadow-[#131312]/20 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Sending..." : "Send Message"}
                  {!loading && <Send className="ml-2 size-5" />}
                </button>
                {submitted && (
                  <div className="flex flex-col items-center gap-2 py-6 text-center">
                    <div className="inline-flex items-center justify-center size-12 rounded-full bg-[#131312] text-white mb-1">
                      <Send className="size-5" />
                    </div>
                    <p className="text-base font-semibold text-[#131312]">Message Sent!</p>
                    <p className="text-sm text-muted-foreground">We'll get back to you within 24 hours.</p>
                  </div>
                )}
              </form>
            </div>

            <div>
              <h2 className="text-4xl md:text-4x1 font-semibold leading-[1.1] tracking-tight mb-8 text-[#131312]">Business Hours</h2>
              <div className="bg-[#F7F4ED] rounded-3xl p-8 mb-8 border border-[#131312]/5 shadow-sm">
                <div className="flex items-center space-x-4 mb-8">
                  <div className="size-14 bg-white border border-[#131312]/10 text-[#131312] rounded-2xl flex items-center justify-center shadow-sm">
                    <Clock className="size-7" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#131312]">We're Here to Help</h3>
                    <p className="text-muted-foreground text-sm">Monday - Friday</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-5 bg-white rounded-xl border border-[#131312]/5 transition-colors hover:bg-white/80">
                    <span className="font-medium text-[#131312]">Monday - Thursday</span>
                    <span className="text-muted-foreground font-medium text-sm">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center p-5 bg-white rounded-xl border border-[#131312]/5 transition-colors hover:bg-white/80">
                    <span className="font-medium text-[#131312]">Friday</span>
                    <span className="text-muted-foreground font-medium text-sm">9:00 AM - 5:00 PM</span>
                  </div>
                  <div className="flex justify-between items-center p-5 bg-white rounded-xl border border-[#131312]/5 transition-colors hover:bg-white/80">
                    <span className="font-medium text-[#131312]">Saturday - Sunday</span>
                    <span className="text-muted-foreground font-medium text-sm">Closed</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#131312] text-white rounded-3xl p-10 shadow-2xl shadow-[#131312]/10 border border-white/5 relative overflow-hidden">
                <div className="relative z-10">
                  <h3 className="text-2xl font-semibold mb-4 text-white">Need a Quote Instead?</h3>
                  <p className="text-white/70 mb-8 leading-relaxed">
                    If you're ready to start a project, use our detailed quote form to get precise pricing within 24 hours.
                  </p>
                  <Link
                    to="/quote"
                    className="inline-flex items-center justify-center px-8 py-4 bg-white text-[#131312] rounded-xl font-bold transition-all hover:bg-white/90 shadow-lg"
                  >
                    Get Instant Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-32 bg-[#F7F4ED]">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <div>
              <h2 className="text-4xl md:text-4xl font-semibold leading-[1.1] tracking-tight text-[#131312] leading-tight mb-8">
                Frequently Asked <br className="hidden lg:block" /> Questions
              </h2>
              <p className="text-[#131312]/70 text-lg max-w-sm">
                Can't find what you're looking for? Reach out to our team for personalized assistance.
              </p>
            </div>

            <div className="flex flex-col items-start p-0 w-full lg:max-w-[586px] flex-grow self-stretch">
              <AccordionItem
                question="What's your minimum order quantity?"
                answer="MOQs vary by product type. Contact us for specific requirements for your project."
              />
              <AccordionItem
                question="Do you ship internationally?"
                answer="Yes, we ship to UK, EU, USA, and other international markets."
              />
              <AccordionItem
                question="How long does production take?"
                answer="Typically 10-15 days after sample approval, depending on order complexity."
              />
              <AccordionItem
                question="Can I get physical samples?"
                answer="Yes, we provide physical samples before production at no additional cost."
              />
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-24 bg-[#131312] text-white overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: `
              radial-gradient(72.35% 197.74% at 70% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%),
              radial-gradient(72.35% 197.74% at 30% 50%, rgba(255, 255, 255, 0.07) 0%, rgba(0, 0, 0, 0) 50%)
            `
          }}
        />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white max-w-4xl mx-auto leading-[1.1]">
            Ready to Create Premium <br /> Packaging?
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Get an instant quote and see how we can elevate your brand with custom packaging solutions
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/quote"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl bg-white text-sm font-medium text-[#131312] transition-colors hover:bg-white/90 shadow-lg sm:h-14 sm:w-auto sm:px-8 sm:text-base"
            >
              Get Instant Quote
              <ArrowRight className="ml-2 size-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex h-12 w-full items-center justify-center rounded-xl border border-white/20 bg-white/10 px-8 text-sm font-medium text-white backdrop-blur-sm transition-colors hover:bg-white/20 sm:h-14 sm:w-auto sm:text-base"
            >
              Schedule Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
